import { PartialFlowStep } from "../steps";
import { getOutgoingStepIds } from "./flow-graph";

/**
 * Counts the unique downstream steps reachable from `stepId` through any
 * outgoing edge (next, condition branches, segment edges, etc.).
 *
 * The traversal is cycle-safe: a visited set is consulted before each recurse,
 * so a degenerate cyclic graph (shouldn't occur in well-formed rules but is
 * possible in malformed flows) returns a finite count instead of looping
 * forever. The starting `stepId` itself is excluded from the count.
 *
 * Used by the advanced flow editor's node-delete confirmation dialog
 * (PRO-958) to warn merchants before a cascading delete removes their
 * downstream chain.
 */
export function countDownstreamSteps(
  stepId: string,
  steps: PartialFlowStep[],
): number {
  const stepsById = new Map<string, PartialFlowStep>();
  for (const step of steps) {
    stepsById.set(step.id, step);
  }

  const start = stepsById.get(stepId);
  if (!start) {
    return 0;
  }

  const visited = new Set<string>();
  const stack: string[] = [...getOutgoingStepIds(start)];

  while (stack.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentId = stack.pop()!;
    if (visited.has(currentId) || currentId === stepId) {
      continue;
    }
    const currentStep = stepsById.get(currentId);
    // Skip dangling references to steps that no longer exist — they can't
    // contribute to the visible downstream chain.
    if (!currentStep) {
      continue;
    }
    visited.add(currentId);
    for (const nextId of getOutgoingStepIds(currentStep)) {
      if (!visited.has(nextId) && nextId !== stepId) {
        stack.push(nextId);
      }
    }
  }

  return visited.size;
}
